import styled from '@emotion/styled'
import { useState } from 'react'

import { SVGWrapper } from '../../../components'
import { IconClose } from '../../../assets/icons'

/*
 * Mobile chrome for the add-investment flow.
 *
 * On a phone the flow takes the whole screen rather than sitting in the app's page shell: the
 * keyboard eats most of the viewport, so the search needs every pixel, and the design puts the
 * Cancel lock-up where the top nav would otherwise be. The shell is fixed and owns its own
 * scrolling, which is also what lets the header and footer stay put while the content runs under
 * them.
 */

const FOOTER_HEIGHT = '42px'
/* Design: both fixed bars set themselves off from the content with the same 10px and shadow. */
const BAR_PADDING = '10px'

export const FocusedShell = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  /* Above the app's top nav and sidebar drawer, which the focused flow replaces. */
  z-index: 1200;
  display: flex;
  flex-direction: column;
  height: 100vh; /* fallback for browsers without dynamic viewport units */
  /* Dynamic viewport so the shell tracks the collapsing URL bar. dvh does NOT account for the
     on-screen keyboard, so AddNewInvestment overrides this height with the measured
     visual-viewport height while the keyboard is open — that is what keeps the footer above it. */
  height: 100dvh;
  /* The keyboard-open height is an exact fit; never let content stretch it. */
  overflow: hidden;
  background-color: #ffffff;
`

export const FocusedHeader = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 20px 20px ${BAR_PADDING};
  background-color: #ffffff;
  transition: box-shadow 0.2s ease;
  box-shadow: ${p => (p.scrolled ? '0 2px 1px 0 rgba(0, 0, 0, 0.1)' : 'none')};
`

/** The Cancel / X lock-up that leaves the flow. One control, so either half of it works. */
export const CancelLockup = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 16px;
  line-height: 22px;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  cursor: pointer;
`

export const FocusedScroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0 20px max(20px, env(safe-area-inset-bottom));
`

/** A fixed footer holding a single text action, drawn above the keyboard by the shell's sizing. */
export const FocusedFooter = styled.button`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  box-sizing: border-box;
  width: 100%;
  min-height: ${FOOTER_HEIGHT};
  padding: ${BAR_PADDING} 20px calc(${BAR_PADDING} + env(safe-area-inset-bottom));
  border: 0;
  background-color: #ffffff;
  box-shadow: 0 -2px 1px 0 rgba(0, 0, 0, 0.1);
  font-family: inherit;
  font-size: 16px;
  line-height: 22px;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  cursor: pointer;
`

/** The screen's heading. Swaps between finding an investment and filling one in. */
export const FocusedHeading = styled.h1`
  margin: 0;
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 30px;
  font-weight: 300;
  line-height: 40px;
  color: ${p => p.theme.darkestGray ?? '#022a3a'};
  word-break: break-word;
`

/**
 * The heading, and the help icon that belongs to it.
 *
 * The icon centres on the heading's line box, which is where its text sits. It owns the heading's
 * bottom spacing for that reason: as padding on the heading itself, that space would count towards
 * what is being centred on and push the icon below the text.
 */
export const HeadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 10px;

  /* The icon sits inside a stack of inline-blocks, each of whose line boxes reserves descender
     space under the glyph. Zeroing the line height they inherit collapses that slack, so the box
     being centred is the glyph and nothing else. */
  > div {
    line-height: 0;
  }
`

export const ListHeading = styled.div`
  padding: 20px 0 5px;
  font-size: 16px;
  line-height: 22px;
  font-weight: ${p => (p.bold ? 700 : 400)};
  color: ${p => (p.bold ? (p.theme.mediumGray ?? '#4a606a') : (p.theme.darkestGray ?? '#022a3a'))};
`

export const EmptyMessage = styled.div`
  padding: 20px 0 0;
  font-size: 16px;
  line-height: 22px;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
`

/**
 * The focused flow's frame: the full-screen surface and the Cancel lock-up pinned to the top of it.
 *
 * `children` are laid out as the surface's own rows, so a screen that wants a fixed footer hands
 * over its scrolling region and its footer side by side.
 *
 * @param {Object} props
 * @param {Function} props.onCancel leaves the flow
 * @param {boolean} [props.scrolled] draws the header's shadow, once content has run under it
 * @param {Object} [props.style] sizing overrides — the shell is pinned to the visible area while
 *   the on-screen keyboard is up
 * @param {string} [props.cancelLabel] what the Cancel lock-up is called to a screen reader
 */
export const FocusedFlow = props => {
  const { onCancel, scrolled = false, style, cancelLabel = 'Cancel', children } = props

  return (
    <FocusedShell style={style}>
      <FocusedHeader scrolled={scrolled}>
        <CancelLockup type='button' aria-label={cancelLabel} onClick={onCancel}>
          Cancel
          <SVGWrapper svg={IconClose} size='large' margin='0px' />
        </CancelLockup>
      </FocusedHeader>
      {children}
    </FocusedShell>
  )
}

/**
 * Whether a scroll container has been scrolled away from the top, so the bar above it can set
 * itself off from the content running underneath — and stay flat while there is nothing under it.
 *
 * @returns {{scrolled: boolean, handleScroll: Function}} an onScroll handler for the container
 */
export const useScrolledUnder = () => {
  const [scrolled, setScrolled] = useState(false)

  const handleScroll = event => setScrolled(event.currentTarget.scrollTop > 0)

  return { scrolled, handleScroll }
}
